import { useEffect, useRef, useState } from "react";

function useRevealOnScroll(ref) {
  useEffect(() => {
    const elements = ref.current?.querySelectorAll("[data-reveal]");
    if (!elements) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            const delay = el.dataset.delay || "0";
            setTimeout(() => {
              el.style.opacity = "1";
              el.style.transform = "translateY(0)";
            }, parseFloat(delay));
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1 },
    );

    elements.forEach((el) => {
      el.style.opacity = "0";
      el.style.transform = "translateY(20px)";
      el.style.transition = "opacity 0.75s ease, transform 0.75s ease";
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);
}

const services = [
  {
    num: "01",
    tag: "First step",
    title: "Single session",
    subtitle: "Sound healing + integration",
    description:
      "A good place to begin. One session that gives you a real taste of the work — live music, sound and inner exploration. We prepare together beforehand so you arrive ready, and integrate what surfaces afterwards so it lands and stays.",
    details: [
      "Preparation call included",
      "90-minute sound healing session",
      "Integration conversation",
      "Online or in Amsterdam",
    ],
    cta: "Book a session",
    accent: false,
  },
  {
    num: "02",
    tag: "Deep work",
    title: "4–6 session package",
    subtitle: "Sound + ceremonial truffles",
    description:
      "For those ready to go deeper. This package weaves together inner work, live sound and one ceremonial truffle session — legal in the Netherlands — held in a safe, intentional container. We prepare thoroughly before and integrate carefully after. This is where lasting change happens.",
    details: [
      "Intake & preparation sessions",
      "One ceremonial truffle session with live music",
      "Sound healing sessions throughout",
      "Integration work to bring it home",
      "Ongoing support between sessions",
    ],
    cta: "Enquire about the package",
    accent: true,
  },
  {
    num: "03",
    tag: "Group experience",
    title: "Group ceremonies",
    subtitle: "Sound + ceremonial truffles",
    description:
      "A shared journey. Small groups gather to experience live music and sound healing alongside a ceremonial truffle microdose. A gentle way to meet the medicine and the music together — and to feel what it's like to go inward in community.",
    details: [
      "Small groups (6–12 people)",
      "Live music throughout",
      "Ceremonial truffle microdose",
      "Opening & closing circle",
      "Amsterdam & retreats",
    ],
    cta: "Join the next ceremony",
    accent: false,
  },
];

function ServiceCard({ service, index }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      data-reveal
      data-delay={`${index * 120}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`
        relative flex flex-col p-8 lg:p-10 transition-all duration-500 cursor-default
        ${
          service.accent
            ? "bg-earth border-earth text-cream"
            : `bg-cream border-sand-dark ${hovered ? "bg-sand" : ""}`
        }
      `}
    >
      {/* Tag */}
      <div className="flex items-center justify-between mb-8">
        <span
          className={`text-[0.62rem] tracking-[0.25em] uppercase font-jost font-light ${
            service.accent ? "text-cream/60" : "text-earth/60"
          }`}
        >
          {service.tag}
        </span>
        <span
          className={`font-cormorant font-light text-4xl leading-none ${
            service.accent ? "text-cream/20" : "text-sand-dark"
          }`}
        >
          {service.num}
        </span>
      </div>

      {/* Title block */}
      <div className="mb-6">
        <h3
          className={`font-cormorant font-light leading-[1.1] mb-2 ${
            service.accent ? "text-cream" : "text-ink"
          }`}
          style={{ fontSize: "clamp(1.6rem, 2vw, 2rem)" }}
        >
          {service.title}
        </h3>
        <p
          className={`font-cormorant italic font-light ${
            service.accent ? "text-cream/70" : "text-earth-light"
          }`}
          style={{ fontSize: "clamp(1rem, 1.2vw, 1.15rem)" }}
        >
          {service.subtitle}
        </p>
      </div>

      {/* Divider */}
      <div
        className={`w-8 h-px mb-6 ${
          service.accent ? "bg-cream/30" : "bg-earth-light/40"
        }`}
      />

      {/* Description */}
      <p
        className={`font-jost font-light leading-[1.85] mb-8 flex-grow ${
          service.accent ? "text-cream/75" : "text-stone"
        }`}
        style={{ fontSize: "0.9rem" }}
      >
        {service.description}
      </p>

      {/* Detail list */}
      <ul className="space-y-2 mb-10">
        {service.details.map((d) => (
          <li
            key={d}
            className={`flex items-start gap-3 font-jost font-light text-[0.82rem] ${
              service.accent ? "text-cream/65" : "text-stone"
            }`}
          >
            <span
              className={`mt-[0.4rem] w-1 h-1 rounded-full flex-shrink-0 ${
                service.accent ? "bg-cream/40" : "bg-earth-light"
              }`}
            />
            {d}
          </li>
        ))}
      </ul>

      {/* CTA */}
      <a
        href="#contact"
        className={`
          inline-block self-start px-7 py-3 text-[0.72rem] tracking-[0.18em] uppercase font-jost font-light
          transition-all duration-300 border
          ${
            service.accent
              ? "border-cream/40 text-cream hover:bg-cream hover:text-earth"
              : "border-ink text-ink hover:bg-ink hover:text-cream"
          }
        `}
      >
        {service.cta}
      </a>
    </div>
  );
}

export const WorkContent = () => {
  const sectionRef = useRef(null);
  useRevealOnScroll(sectionRef);

  return (
    <section
      id="work"
      ref={sectionRef}
      className="bg-cream py-24 lg:py-32 px-8 lg:px-16"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div className="mb-16 lg:mb-20">
          <div>
            <p
              data-reveal
              data-delay="0"
              className="text-[0.65rem] tracking-[0.3em] uppercase text-earth-light opacity-60 mb-4 font-jost font-light"
            >
              The work
            </p>
            <h2
              data-reveal
              data-delay="80"
              className="font-cormorant font-light leading-[1.1] mb-8"
              style={{ fontSize: "clamp(2.4rem, 3.5vw, 3.8rem)" }}
            >
              Ways to work <em className="italic text-earth">together</em>
            </h2>
          </div>

          <p
            data-reveal
            data-delay="160"
            className="mt-6 font-jost font-light text-stone leading-[1.85] max-w-[560px]"
            style={{ fontSize: "clamp(0.9rem, 1.05vw, 1rem)" }}
          >
            Whether you're curious and want to dip a toe in, or ready to go deep
            — there's a right entry point for where you are right now. All
            sessions are held with care, intention and live music.
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border border-sand-dark">
          {services.map((service, i) => (
            <div
              key={service.num}
              className={`${i < services.length - 1 ? "" : ""}`}
            >
              <ServiceCard service={service} index={i} />
            </div>
          ))}
        </div>

        {/* Footer note */}
        <p
          data-reveal
          data-delay="400"
          className="mt-10 font-jost font-light text-stone/60 text-[0.78rem] tracking-wide text-center"
        >
          Ceremonial truffle sessions are legal in the Netherlands. All work is
          held in a safe, prepared and intentional container.
        </p>
      </div>
    </section>
  );
};
